import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const TOKEN_KEY = "pos_token";
export const STORE_KEY = "pos_store_id";

export const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const storeId = localStorage.getItem(STORE_KEY);
  config.headers = config.headers || {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (storeId) config.headers["X-Store-Id"] = storeId;
  // Bulk / import endpoints process thousands of rows server-side and
  // legitimately take longer than the default 30s ceiling. Give them 5
  // minutes unless the caller explicitly supplied their own timeout.
  const url = (config.url || "").toLowerCase();
  const isBulk =
    url.includes("/import") ||
    url.includes("/migrate") ||
    url.includes("/backfill") ||
    url.includes("/cleanup") ||
    url.includes("/batch");
  if (isBulk && config.timeout === 30000) {
    config.timeout = 5 * 60 * 1000;
  }
  return config;
});

// Transient-error resilience — retry ONCE on idempotent GETs when the browser
// reports a network error, timeout, or a 502/503/504 from the edge. This
// transparently rides through:
//   • a ~3s supervisor hot-reload restart after a backend code change
//   • a momentary ingress hiccup
//   • a dropped connection on mobile / flaky wifi
// Non-idempotent writes (POST/PUT/DELETE/PATCH) are NEVER retried automatically
// so we don't accidentally double-charge a sale.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const status = error.response?.status;
    const isTransient =
      error.code === "ECONNABORTED" ||          // timeout
      error.code === "ERR_NETWORK" ||           // network-down / CORS failure pre-response
      error.message === "Network Error" ||      // axios classic
      status === 502 || status === 503 || status === 504;
    if (method === "get" && isTransient && !cfg.__retried) {
      cfg.__retried = true;
      await new Promise((r) => setTimeout(r, 900));
      return api(cfg);
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  return String(detail);
}

export function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------- Brisbane (AEST, UTC+10, no DST) date/time formatters ----------
//
// All timestamps in the DB are stored in UTC (ISO 8601, e.g.
// "2026-06-22T02:15:57.559666+00:00"). Browsers' default
// `toLocaleString()` renders in whatever timezone the host machine is set
// to — which is wrong when a counter terminal is in another region (e.g.
// the dev preview server runs in UTC). These helpers lock formatting to
// Brisbane time so every cashier sees the same wall-clock time on every
// device.
//
// Currently the store timezone is also captured per-store on the backend
// (Store.timezone). When we eventually need true multi-region support
// across stores, swap the hard-coded "Australia/Brisbane" for the active
// store's tz — but every existing iRepair franchise is in AEST so this
// stays simple for now.
export const POS_TZ = "Australia/Brisbane";

const dt = (input) => (input ? new Date(input) : null);

// "22 Jun 2026, 12:15 pm"
export function fmtDateTime(input) {
  const d = dt(input);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    timeZone: POS_TZ,
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

// "22 Jun 2026"
export function fmtDate(input) {
  const d = dt(input);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", {
    timeZone: POS_TZ, day: "2-digit", month: "short", year: "numeric",
  });
}

// "12:15 pm"
export function fmtTime(input) {
  const d = dt(input);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-AU", {
    timeZone: POS_TZ, hour: "numeric", minute: "2-digit", hour12: true,
  });
}

// "2026-06-22 12:15" — for compact rows that previously sliced the ISO string.
export function fmtShort(input) {
  const d = dt(input);
  if (!d || isNaN(d.getTime())) return "";
  // en-CA returns YYYY-MM-DD; pair with 24h HH:mm so columns line up nicely.
  const datePart = d.toLocaleDateString("en-CA", { timeZone: POS_TZ });
  const timePart = d.toLocaleTimeString("en-GB", {
    timeZone: POS_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return `${datePart} ${timePart}`;
}

export const STATUS_LABELS = {
  new: "New",
  in_progress: "In Progress",
  waiting_parts: "Waiting Parts",
  waiting_customer: "Waiting on Customer",
  ready: "Ready",
  completed: "Completed",
  picked_up: "Picked Up",
  cancelled: "Cancelled",
};

export const STATUS_COLORS = {
  new: "#1E5FEB",
  in_progress: "#09090B",
  waiting_parts: "#C98B24",
  waiting_customer: "#7C3AED",
  ready: "#2F8F6E",
  completed: "#1E5FEB",
  picked_up: "#4B5563",
  cancelled: "#DC2626",
};

export const STATUS_ORDER = ["new", "in_progress", "waiting_parts", "waiting_customer", "ready", "completed", "picked_up", "cancelled"];

// Refunds recorded directly on a POS sale (not via an invoice) as payment-history rows.
export function saleRefundRows(sale) {
  return (sale?.refunds || [])
    .filter((r) => r.source !== "invoice")
    .map((r) => ({
      method: r.tyro_meta ? "tyro" : (r.method === "card_manual" ? "card" : r.method || "cash"),
      amount: -Math.abs(r.amount || 0),
      paid_at: r.refunded_at,
      cashier: r.refunded_by || "Staff",
      is_refund: true,
      note: [r.reason ? `Refund — ${r.reason}` : "Refund", r.customer_name ? `credit to ${r.customer_name}` : ""].filter(Boolean).join(" · "),
      tyro_meta: r.tyro_meta || null,
    }));
}

export const TRADE_IN_PAYOUT_LABELS = {
  cash: "Cash", store_credit: "Store credit", bank_transfer: "Bank transfer", payid: "PayID",
};

// Linked online invoice (if any) + warranty data for a POS sale, fetched in parallel.
export async function loadSaleInvoiceExtras(fullSale) {
  const ticketId = (fullSale.items || []).find((it) => it.repair_ticket_id)?.repair_ticket_id;
  const linkedP = ticketId
    ? api.get(`/online-invoices/by-ticket/${ticketId}`)
      .then(({ data }) => (data?.items?.length ? api.get(`/online-invoices/${data.items[0].id}/detail`).then((r) => r.data) : null))
      .catch(() => null)
    : Promise.resolve(null);
  const warrantyP = api.get(`/sales/${fullSale.id}/warranty`).then((r) => r.data).catch(() => null);
  const [linkedInvoice, warranty] = await Promise.all([linkedP, warrantyP]);
  return { linkedInvoice, warranty: warranty || { devices: [], accessories_warranty: [], item_warranties: [] } };
}
